import makeApiCall from "./master/axiosIndex";

// Daily Execution Approvals — a site day (project + date) is submitted,
// reviewed at one or two levels, approved, or rejected with remarks.

export type DayApprovalStatus =
  | ""
  | "Submitted"
  | "InReview"
  | "Approved"
  | "Rejected"
  | "Recalled"
  | "Reopened";

export interface DayApprovalState {
  dprId: number; // 0 = never submitted
  projectId: number;
  reportDate: string;
  status: DayApprovalStatus;
  round: number;
  currentLevel: number;
  levelCount: number;
  locked: boolean;
  submittedBy: number;
  submittedByName: string;
  submittedAt: string;
  submitRemarks: string;
  lastAction: string;
  lastActionByName: string;
  lastActionAt: string;
  lastRemarks: string;
  openNoteCount: number;
  canSubmit: boolean;
  canRecall: boolean;
  canApprove: boolean;
  canReject: boolean;
  canReopen: boolean;
  canNote: boolean;
  flagCount: number;
}

export interface DailyApprovalRow {
  dprId: number;
  projectId: number;
  projectName: string;
  reportDate: string;
  status: DayApprovalStatus;
  round: number;
  currentLevel: number;
  levelCount: number;
  submittedBy: number;
  submittedByName: string;
  submittedAt: string;
  daysWaiting: number;
  faceCount: number;
  boqLineCount: number;
  materialTaskDayCount: number;
  machineCount: number;
  flagCount: number;
  lastActionAt: string;
  lastActionByName: string;
  lastRemarks: string;
  canAct: boolean;
}

export type DailyApprovalTab = "MINE" | "PENDING" | "APPROVED" | "REJECTED";

export interface ReviewFlag {
  section: "civil" | "material" | "mechanical" | "stock" | "norm";
  kind: string;
  severity: "high" | "medium";
  title: string;
  detail: string;
  anchor: string;
}

export interface ReviewNote {
  noteId: number;
  round: number;
  section: string;
  refType: string;
  refId: number;
  refLabel: string;
  note: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  resolved: boolean;
  resolvedByName: string;
  resolvedAt: string;
}

export interface ReviewAction {
  actionId: number;
  round: number;
  level: number;
  action: string;
  userId: number;
  userName: string;
  isAdmin: boolean;
  remarks: string;
  createdAt: string;
}

/** One material's position in the project store for the day. */
export interface ReviewStockRow {
  materialId: number;
  materialName: string;
  productCode: string;
  categoryName: string;
  uomName: string;
  openingQty: string;
  openingValue: string;
  purchasedQty: string;
  purchasedRate: string;
  purchasedValue: string;
  issuedQty: string;
  issuedValue: string;
  closingQty: string;
  closingValue: string;
  avgRate: string;
  taskIssuedQty: string;
  varianceQty: string;
  varianceState: string;
  normRate: string;
  purchaseAboveNorm: boolean;
  targetQty: string;
  targetState: string;
  remarks: string;
}

// ---- the ABS cost abstract: task -> group -> (sub-group) -> line ----

export interface ReviewCostTotals {
  targetAmount: string;
  targetPerUnit: string;
  actualAmount: string;
  actualPerUnit: string;
  variancePercent: string;
}

export interface ReviewCostLine {
  label: string;
  subLabel: string;
  uomName: string;
  normPerUnit: string;
  targetQty: string;
  targetRate: string;
  targetAmount: string;
  targetPerUnit: string;
  actualQty: string;
  actualRate: string;
  actualAmount: string;
  actualPerUnit: string;
  variancePercent: string;
  /** Carried so the existing per-line note anchors work on this tab too. */
  refType: string;
  refId: number;
  anchor: string;
  flag: string;
  /**
   * The client ABS sheet's context columns. Material reads pucFactor +
   * wastage; machinery reuses pucFactor as 'Non Prod Fac' and adds the other
   * two factors plus the production norm. wastage is the stored FRACTION —
   * 0.02 is 2%, so a renderer multiplies.
   */
  pucFactor: string;
  wastage: string;
  utilizationFactor: string;
  applicationFactor: string;
  prodNormUnit: string;
  prodNormHrs: string;
  /** '' on a normal row; the equipment head key on a machinery child row. */
  head: string;
  /** Machinery only: the eight cost heads that sit beneath their machine. */
  children: ReviewCostLine[];
}

export interface ReviewCostSubGroup {
  key: string;
  label: string;
  lines: ReviewCostLine[];
  totals: ReviewCostTotals;
}

/** key: MATERIAL | MACHINERY | OUTSOURCE | PRW. Only Material sub-groups. */
export interface ReviewCostGroup {
  key: string;
  label: string;
  subGroups: ReviewCostSubGroup[];
  lines: ReviewCostLine[];
  totals: ReviewCostTotals;
}

export interface ReviewCostTask {
  taskId: number;
  taskSequenceId: string;
  taskName: string;
  activityName: string;
  producedQty: string;
  workUomName: string;
  groups: ReviewCostGroup[];
  totals: ReviewCostTotals;
  /** 'AE-0101/B3.1.1(1)' -> 'B3.1.1(1)' — the ABS sheet's 'BOQ Code'. */
  boqCode: string;
}

export interface DailyReview {
  projectId: number;
  projectName: string;
  reportDate: string;
  state: DayApprovalState;
  summary: {
    faceCount: number;
    boqLineCount: number;
    materialTaskDayCount: number;
    machineCount: number;
    flagCount: number;
    materialAmount: string;
    machineryAmount: string;
    costTaskCount: number;
    costTargetAmount: string;
    costActualAmount: string;
    costVariancePercent: string;
    /** '' unless every task on the day shares one work unit — see the server. */
    costPerUnitLabel: string;
    stockMaterialCount: number;
    /**
     * The store's closing value. Deliberately NOT part of materialAmount: the
     * ledger's issue value is the same material leaving the store that the task
     * issues already account for, so adding the two double counts.
     */
    stockClosingValue: string;
  };
  flags: ReviewFlag[];
  civil: any[];
  materials: any[];
  mechanical: any[];
  stock: ReviewStockRow[];
  cost: ReviewCostTask[];
  costTotals: ReviewCostTotals;
  notes: ReviewNote[];
  history: ReviewAction[];
}

/** Where a project's approval chain comes from. */
export type ApprovalSource = "PROJECT" | "SITE" | "ORG" | "AUTO" | "NONE";

export interface ApprovalSetupUser {
  userId: number;
  userName: string;
  projectUserType: string;
  /** Holds the role, but lacks approve on the Project module — a warning, not a filter. */
  canActOnModule: boolean;
}

export interface ApprovalSetupLevel {
  level: number;
  levelName: string;
  levelRole: number;
  roleName: string;
  /** The level's role matched nobody on this project; the rung is kept, not dropped. */
  unresolved: boolean;
  approvers: ApprovalSetupUser[];
}

export interface ApprovalSetup {
  projectId: number;
  source: ApprovalSource;
  hierarchyId: number;
  levelCount: number;
  overrideActive: boolean;
  warnings: string[];
  levels: ApprovalSetupLevel[];
  projectUsers: ApprovalSetupUser[];
  message: string;
}

// ------------------------------------------------------------ normalisers
// proto3 omits false / 0 / '' on the wire — every field gets its default here.
const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const bool = (v: unknown) => v === true;
const arr = (v: unknown): any[] => (Array.isArray(v) ? v : []);

export const normaliseState = (raw: any): DayApprovalState => ({
  dprId: num(raw?.dprId),
  projectId: num(raw?.projectId),
  reportDate: str(raw?.reportDate),
  status: str(raw?.status) as DayApprovalStatus,
  round: num(raw?.round),
  currentLevel: num(raw?.currentLevel),
  levelCount: num(raw?.levelCount),
  locked: bool(raw?.locked),
  submittedBy: num(raw?.submittedBy),
  submittedByName: str(raw?.submittedByName),
  submittedAt: str(raw?.submittedAt),
  submitRemarks: str(raw?.submitRemarks),
  lastAction: str(raw?.lastAction),
  lastActionByName: str(raw?.lastActionByName),
  lastActionAt: str(raw?.lastActionAt),
  lastRemarks: str(raw?.lastRemarks),
  openNoteCount: num(raw?.openNoteCount),
  canSubmit: bool(raw?.canSubmit),
  canRecall: bool(raw?.canRecall),
  canApprove: bool(raw?.canApprove),
  canReject: bool(raw?.canReject),
  canReopen: bool(raw?.canReopen),
  canNote: bool(raw?.canNote),
  flagCount: num(raw?.flagCount),
});

export const emptyState = (projectId: number, reportDate: string): DayApprovalState => ({
  ...normaliseState({ projectId, reportDate }),
  canSubmit: true,
});

export const normaliseRow = (raw: any): DailyApprovalRow => ({
  dprId: num(raw?.dprId),
  projectId: num(raw?.projectId),
  projectName: str(raw?.projectName),
  reportDate: str(raw?.reportDate),
  status: str(raw?.status) as DayApprovalStatus,
  round: num(raw?.round),
  currentLevel: num(raw?.currentLevel),
  levelCount: num(raw?.levelCount),
  submittedBy: num(raw?.submittedBy),
  submittedByName: str(raw?.submittedByName),
  submittedAt: str(raw?.submittedAt),
  daysWaiting: num(raw?.daysWaiting),
  faceCount: num(raw?.faceCount),
  boqLineCount: num(raw?.boqLineCount),
  materialTaskDayCount: num(raw?.materialTaskDayCount),
  machineCount: num(raw?.machineCount),
  flagCount: num(raw?.flagCount),
  lastActionAt: str(raw?.lastActionAt),
  lastActionByName: str(raw?.lastActionByName),
  lastRemarks: str(raw?.lastRemarks),
  canAct: bool(raw?.canAct),
});

const normaliseNote = (raw: any): ReviewNote => ({
  noteId: num(raw?.noteId),
  round: num(raw?.round),
  section: str(raw?.section),
  refType: str(raw?.refType),
  refId: num(raw?.refId),
  refLabel: str(raw?.refLabel),
  note: str(raw?.note),
  createdBy: num(raw?.createdBy),
  createdByName: str(raw?.createdByName),
  createdAt: str(raw?.createdAt),
  resolved: bool(raw?.resolved),
  resolvedByName: str(raw?.resolvedByName),
  resolvedAt: str(raw?.resolvedAt),
});

const costTotals = (raw: any): ReviewCostTotals => ({
  targetAmount: str(raw?.targetAmount),
  targetPerUnit: str(raw?.targetPerUnit),
  actualAmount: str(raw?.actualAmount),
  actualPerUnit: str(raw?.actualPerUnit),
  variancePercent: str(raw?.variancePercent),
});

const costLine = (raw: any, withChildren = true): ReviewCostLine => ({
  label: str(raw?.label),
  subLabel: str(raw?.subLabel),
  uomName: str(raw?.uomName),
  normPerUnit: str(raw?.normPerUnit),
  targetQty: str(raw?.targetQty),
  targetRate: str(raw?.targetRate),
  targetAmount: str(raw?.targetAmount),
  targetPerUnit: str(raw?.targetPerUnit),
  actualQty: str(raw?.actualQty),
  actualRate: str(raw?.actualRate),
  actualAmount: str(raw?.actualAmount),
  actualPerUnit: str(raw?.actualPerUnit),
  variancePercent: str(raw?.variancePercent),
  refType: str(raw?.refType),
  refId: num(raw?.refId),
  anchor: str(raw?.anchor),
  flag: str(raw?.flag),
  pucFactor: str(raw?.pucFactor),
  wastage: str(raw?.wastage),
  utilizationFactor: str(raw?.utilizationFactor),
  applicationFactor: str(raw?.applicationFactor),
  prodNormUnit: str(raw?.prodNormUnit),
  prodNormHrs: str(raw?.prodNormHrs),
  head: str(raw?.head),
  // One level deep by construction (machine -> its heads). Recursing once and
  // stopping is deliberate: a stray nested payload cannot build a deep tree,
  // and nothing downstream reads past the first level.
  children: withChildren && Array.isArray(raw?.children)
    ? raw.children.map((child: any) => costLine(child, false))
    : [],
});

export const normaliseReview = (raw: any): DailyReview => ({
  projectId: num(raw?.projectId),
  projectName: str(raw?.projectName),
  reportDate: str(raw?.reportDate),
  state: normaliseState(raw?.state),
  summary: {
    faceCount: num(raw?.summary?.faceCount),
    boqLineCount: num(raw?.summary?.boqLineCount),
    materialTaskDayCount: num(raw?.summary?.materialTaskDayCount),
    machineCount: num(raw?.summary?.machineCount),
    flagCount: num(raw?.summary?.flagCount),
    materialAmount: str(raw?.summary?.materialAmount),
    machineryAmount: str(raw?.summary?.machineryAmount),
    costTaskCount: num(raw?.summary?.costTaskCount),
    costTargetAmount: str(raw?.summary?.costTargetAmount),
    costActualAmount: str(raw?.summary?.costActualAmount),
    costVariancePercent: str(raw?.summary?.costVariancePercent),
    costPerUnitLabel: str(raw?.summary?.costPerUnitLabel),
    stockMaterialCount: num(raw?.summary?.stockMaterialCount),
    stockClosingValue: str(raw?.summary?.stockClosingValue),
  },
  flags: arr(raw?.flags).map((f) => ({
    section: str(f?.section) as ReviewFlag["section"],
    kind: str(f?.kind),
    severity: (str(f?.severity) || "medium") as ReviewFlag["severity"],
    title: str(f?.title),
    detail: str(f?.detail),
    anchor: str(f?.anchor),
  })),
  civil: arr(raw?.civil).map((e) => ({
    ...e,
    entryId: num(e?.entryId),
    activityId: num(e?.activityId),
    slots: arr(e?.slots).map((s) => ({ ...s, stretches: arr(s?.stretches) })),
    lines: arr(e?.lines).map((l) => ({ ...l, taskId: num(l?.taskId), isMonitored: bool(l?.isMonitored), stretchActuals: arr(l?.stretchActuals) })),
  })),
  materials: arr(raw?.materials).map((t) => ({ ...t, taskId: num(t?.taskId), lines: arr(t?.lines) })),
  cost: arr(raw?.cost).map((task) => ({
    taskId: num(task?.taskId),
    taskSequenceId: str(task?.taskSequenceId),
    taskName: str(task?.taskName),
    activityName: str(task?.activityName),
    producedQty: str(task?.producedQty),
    workUomName: str(task?.workUomName),
    boqCode: str(task?.boqCode),
    groups: arr(task?.groups).map((group) => ({
      key: str(group?.key),
      label: str(group?.label),
      subGroups: arr(group?.subGroups).map((sub) => ({
        key: str(sub?.key),
        label: str(sub?.label),
        lines: arr(sub?.lines).map((line: any) => costLine(line)),
        totals: costTotals(sub?.totals),
      })),
      lines: arr(group?.lines).map((line: any) => costLine(line)),
      totals: costTotals(group?.totals),
    })),
    totals: costTotals(task?.totals),
  })),
  costTotals: costTotals(raw?.costTotals),
  stock: arr(raw?.stock).map((row) => ({
    materialId: num(row?.materialId),
    materialName: str(row?.materialName),
    productCode: str(row?.productCode),
    categoryName: str(row?.categoryName),
    uomName: str(row?.uomName),
    openingQty: str(row?.openingQty),
    openingValue: str(row?.openingValue),
    purchasedQty: str(row?.purchasedQty),
    purchasedRate: str(row?.purchasedRate),
    purchasedValue: str(row?.purchasedValue),
    issuedQty: str(row?.issuedQty),
    issuedValue: str(row?.issuedValue),
    closingQty: str(row?.closingQty),
    closingValue: str(row?.closingValue),
    avgRate: str(row?.avgRate),
    taskIssuedQty: str(row?.taskIssuedQty),
    varianceQty: str(row?.varianceQty),
    varianceState: str(row?.varianceState),
    normRate: str(row?.normRate),
    purchaseAboveNorm: bool(row?.purchaseAboveNorm),
    targetQty: str(row?.targetQty),
    targetState: str(row?.targetState),
    remarks: str(row?.remarks),
  })),
  mechanical: arr(raw?.mechanical).map((t) => ({
    ...t,
    taskId: num(t?.taskId),
    machines: arr(t?.machines).map((m) => ({ ...m, assetId: num(m?.assetId), heads: arr(m?.heads) })),
  })),
  notes: arr(raw?.notes).map(normaliseNote),
  history: arr(raw?.history).map((a) => ({
    actionId: num(a?.actionId),
    round: num(a?.round),
    level: num(a?.level),
    action: str(a?.action),
    userId: num(a?.userId),
    userName: str(a?.userName),
    isAdmin: bool(a?.isAdmin),
    remarks: str(a?.remarks),
    createdAt: str(a?.createdAt),
  })),
});

const setupUser = (raw: any): ApprovalSetupUser => ({
  userId: num(raw?.userId),
  userName: str(raw?.userName),
  projectUserType: str(raw?.projectUserType),
  canActOnModule: bool(raw?.canActOnModule),
});

// The levels come back already ordered and already numbered by the server —
// they are whatever depth the masters hierarchy has, so this no longer
// manufactures a fixed [1, 2].
export const normaliseSetup = (raw: any): ApprovalSetup => ({
  projectId: num(raw?.projectId),
  source: (str(raw?.source) || "NONE") as ApprovalSource,
  hierarchyId: num(raw?.hierarchyId),
  levelCount: num(raw?.levelCount),
  overrideActive: bool(raw?.overrideActive),
  warnings: arr(raw?.warnings).map(str),
  levels: arr(raw?.levels)
    .map((level) => ({
      level: num(level?.level),
      levelName: str(level?.levelName),
      levelRole: num(level?.levelRole),
      roleName: str(level?.roleName),
      unresolved: bool(level?.unresolved),
      approvers: arr(level?.approvers).map(setupUser),
    }))
    .sort((a, b) => a.level - b.level),
  projectUsers: arr(raw?.projectUsers).map(setupUser),
  message: str(raw?.message),
});

export { normaliseNote };

// ------------------------------------------------------------------ calls
type ActionBody = { dprId: number; round: number; level?: number; remarks?: string };

export const dailyApprovalApis = {
  submit: (body: { projectId: number; reportDate: string; remarks?: string }): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/submit`, method: "post", data: body }),
  recall: (body: ActionBody): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/recall`, method: "post", data: body }),
  approve: (body: ActionBody): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/approve`, method: "post", data: body }),
  reject: (body: ActionBody): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/reject`, method: "post", data: body }),
  reopen: (body: ActionBody): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/reopen`, method: "post", data: body }),
  bulkApprove: (items: ActionBody[]): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/bulk-approve`, method: "post", data: { items } }),
  list: (params: {
    tab: DailyApprovalTab;
    projectId?: number | string;
    fromDate?: string;
    toDate?: string;
    pageNo?: number;
    pageSize?: number;
  }): Promise<any> => {
    const query: Record<string, any> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && `${value}` !== "") query[key] = value;
    });
    return makeApiCall({ url: `/daily-approvals/list`, method: "get", params: query });
  },
  review: (projectId: number | string, date: string): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/review/${projectId}`, method: "get", params: { date } }),
  states: (projectId: number | string, fromDate: string, toDate: string): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/states/${projectId}`, method: "get", params: { fromDate, toDate } }),
  saveNote: (body: {
    dprId: number;
    op: "add" | "resolve" | "reopen" | "delete";
    noteId?: number;
    section?: string;
    refType?: string;
    refId?: number;
    refLabel?: string;
    note?: string;
  }): Promise<any> => makeApiCall({ url: `/daily-approvals/notes`, method: "post", data: body }),
  getSetup: (projectId: number | string): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/setup/${projectId}`, method: "get" }),
  saveSetup: (body: { projectId: number; levels: Array<{ level: number; userIds: number[] }> }): Promise<any> =>
    makeApiCall({ url: `/daily-approvals/setup`, method: "post", data: body }),
};
